import {Injectable} from '@angular/core';
import { AUNSettings, EdlPortal, PortalHelpers, PortalSettings, User } from '@edl-development/edl-common';
import { A48ActivityType } from 'src/app/model/enum/a48-activity-type.enum';
import { A48CourseTypes } from 'src/app/model/enum/a48-sub-type.enum';
import { AccessRoleHelper } from 'src/app/model/helpers/access-role.helper';
import { UserHelper } from 'src/app/model/helpers/user.helper';

const DEFAULT_ACTIVITY_TYPE = A48ActivityType.HOURS;

@Injectable()
export class PortalHelpersService {
    isAct48Only(portalSettings: PortalSettings) {
        return PortalHelpers.isAct48Only(portalSettings);
    }

    isAct48HoursOnly(portalSettings: PortalSettings) {
        return PortalHelpers.isAct48HoursOnly(portalSettings);
    }

    getAUNSettings(portalSettings: PortalSettings, aun: string) {
        return portalSettings.AUNSettings?.find(setting => setting.AUN?.trim() === aun?.trim());
    }

    // Needed for case when the AUN for a item no longer exists on the given portal
    createAUNSettings(aun: string, currentActivityType: A48ActivityType = DEFAULT_ACTIVITY_TYPE) {
        const aunSettings = {
            AUNName: '(Archived) AUN',
            AUN: aun,
            IsHoursEnabled: currentActivityType == A48ActivityType.HOURS,
            IsCreditsEnabled: currentActivityType == A48ActivityType.CREDITS,
        } as AUNSettings;

        return aunSettings;
    }

    getPortalName(portal: EdlPortal) {
        return PortalHelpers.getPortalName(portal);
    }

    getAUNSettingsForItem(portalSettings: PortalSettings, currentAUN?: string, currentActivityType?: A48ActivityType) {
        const aunSettingsForItem = [...(portalSettings.AUNSettings ?? [])];
        const currentAUNSetting = this.getAUNSettings(portalSettings, currentAUN);

        if (currentAUNSetting) {
            return { aunSettingsForItem, currentAUNSetting }
        } else if (currentAUN) {
            const archivedAUNSettings = this.createAUNSettings(currentAUN, currentActivityType);
            aunSettingsForItem.push(archivedAUNSettings);

            return { aunSettingsForItem, currentAUNSetting: archivedAUNSettings };
        } else {
            return { aunSettingsForItem, currentAUNSetting: aunSettingsForItem.length === 1 ? aunSettingsForItem[0] : null };
        }
    }

    isValidActivityType(currentActivityType?: A48ActivityType|number|string, aunSettings?: AUNSettings) {
        return !currentActivityType || 
            (aunSettings?.IsHoursEnabled && currentActivityType == A48ActivityType.HOURS) ||
            (aunSettings?.IsCreditsEnabled && currentActivityType == A48ActivityType.CREDITS);
    }

    getValidActivityTypes(types: A48CourseTypes, aunSettings?: AUNSettings, currentActivityType?: A48ActivityType|number|string) {
        return types.activityTypes.filter(a => currentActivityType == a.id || this.isValidActivityType(a.id, aunSettings));
    }

    canViewComplianceItems(portalSettings: PortalSettings) {
        return portalSettings.ComplyEnabled || portalSettings.ComplyAct48Enabled
    }

    canViewReports(portalSettings: PortalSettings, user: User) {
        return portalSettings.ComplyAct48Enabled &&
            (AccessRoleHelper.isSupervisor(user.AccessRoleID) || 
            UserHelper.isEmergencyCert(user) ||
            UserHelper.isParaeducator(user) || 
            !!user.PPID);
    }

    canViewSyncUsers(portalSettings: PortalSettings) {
        return portalSettings.UserSyncEnabled;
    }
}